import ChargePrice from "entities/chargePrice";
import ChargeType from "entities/chargeType";
import ChargeValidity from "entities/chargeValidity";
import { chargeValidityLabel } from "./chargeType";
import type EnumService from "services/enumService";
import type PriceListService from "services/priceListService";

const chargeValidityOrder = [
  ChargeValidity.Annual,
  ChargeValidity.Days30,
  ChargeValidity.Days10,
  ChargeValidity.SingleDay,
  ChargeValidity.Hours24,
];

function isPriceListValid(validSince?: Date, validUntil?: Date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (!validSince || validSince <= today) && (!validUntil || validUntil >= today);
}

export function buildPriceListChargeTypes(priceListService: PriceListService, enumService: EnumService): ChargeType[] {
  const chargeTypes = enumService.values("charge_types");
  const fuelTypes = enumService.values("fuel_types");

  return priceListService.chargePriceLists.flatMap(priceList => {
    const itemsByChargeType = new Map<string, typeof priceList.items>();

    priceList.items.forEach(item => {
      const group = itemsByChargeType.get(item.chargeTypeId);
      if (group) {
        group.push(item);
      } else {
        itemsByChargeType.set(item.chargeTypeId, [item]);
      }
    });

    const rows = Array.from(itemsByChargeType.entries()).map(([chargeTypeId, items]) => {
      const chargeType = chargeTypes.find(x => x.id === chargeTypeId);
      const result = new ChargeType();

      result.id = `${priceList.id}_${chargeTypeId}`;
      result.chargeCode = items[0].chargeCode;
      result.name =
        (chargeType?.description ? chargeType.description : undefined) ??
        chargeValidityLabel(chargeType?.validFor) ??
        items[0].chargeCode;
      if (chargeType) {
        result.validFor = chargeType.validFor;
        result.notifyDaysBefore = chargeType.notifyDaysBefore;
      }
      result.validSince = priceList.validSinceDate;
      result.validUntil = priceList.validUntilDate;
      result.isValid = !chargeType?.isDeleted && isPriceListValid(priceList.validSinceDate, priceList.validUntilDate);
      result.prices = items.map(item => {
        const price = new ChargePrice(fuelTypes.find(fuelType => fuelType.id === item.fuelTypeId)?.fuelType);
        price.price = item.price;
        return price;
      });

      return result;
    });

    // The API does not guarantee the order of the price list items, so it has to be fixed here.
    return rows.sort((a, b) => chargeValidityOrder.indexOf(a.validFor) - chargeValidityOrder.indexOf(b.validFor));
  });
}
